<script setup lang="ts">
/**
 * An approved change on its way out of Lighthouse: a live bar while it runs ("1,240 of 4,465 · about 2 min left"),
 * then what happened: all made, or which ones couldn't be made and why (grouped, with a few examples).
 *
 * Polls `GET /changesets/:id/progress` while it's applying, slower in a background tab and after errors, and stops
 * once it settles (`settled` tells the card). The work runs on the server: leaving the page doesn't stop it, and
 * coming back picks the bar up where it is.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { agentApi, type ChangesetProgress } from '../api'
import { useCardMode } from '../cardMode'
import { SLOW_AFTER_MS, isSettled, pollDelay, progressView, showsProgress } from '../changesProgress'

const props = defineProps<{ changesetId: string; initial?: ChangesetProgress | null; bulk?: boolean }>()
const emit = defineEmits<{ settled: [progress: ChangesetProgress] }>()

const { phone } = useCardMode()
const progress = ref<ChangesetProgress | null>(props.initial ?? null)
const slow = ref(false)
const open = ref(false)
let timer: ReturnType<typeof setTimeout> | null = null
let slowTimer: ReturnType<typeof setTimeout> | null = null
let errors = 0
let alive = true

const visible = computed(() => showsProgress(progress.value, { bulk: !!props.bulk, slow: slow.value }))
const view = computed(() => (progress.value ? progressView(progress.value) : null))
const reasons = computed(() => progress.value?.failures ?? [])

async function poll() {
    timer = null
    try {
        const p = await agentApi().getChangesetProgress(props.changesetId)
        if (!alive) return
        errors = 0
        const was = progress.value
        progress.value = p
        if (isSettled(p)) {
            if (!was || !isSettled(was)) emit('settled', p)
            return
        }
    } catch {
        errors += 1
    }
    schedule()
}

function schedule() {
    if (!alive || timer) return
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden'
    timer = setTimeout(poll, pollDelay({ hidden, errors }))
}

function onVisible() {
    if (document.visibilityState !== 'visible' || isSettled(progress.value)) return
    if (timer) clearTimeout(timer)
    timer = null
    void poll()
}

onMounted(() => {
    slowTimer = setTimeout(() => { slow.value = true }, SLOW_AFTER_MS)
    document.addEventListener('visibilitychange', onVisible)
    // Numbers that came with the change's view are fresh: ask again after one interval, not straight away.
    if (!isSettled(progress.value)) {
        if (props.initial) schedule()
        else void poll()
    }
})
onBeforeUnmount(() => {
    alive = false
    if (timer) clearTimeout(timer)
    if (slowTimer) clearTimeout(slowTimer)
    document.removeEventListener('visibilitychange', onVisible)
})
// A fresh view of the change (the card reloaded it) carries newer numbers.
watch(() => props.initial, (p) => {
    if (!p) return
    if (!progress.value || p.finished >= progress.value.finished) progress.value = p
    if (!isSettled(progress.value)) schedule()
})
</script>

<template>
    <div
        v-if="visible && view"
        class="cp"
        :class="[`is-${view.tone}`, { 'is-bubble': phone }]"
        data-testid="changes-progress"
        :data-state="progress?.state"
        :aria-busy="view.tone === 'live'"
    >
        <div class="cp-top">
            <span class="cp-mark" aria-hidden="true">
                <span v-if="view.tone === 'live'" class="cp-pulse" />
                <svg v-else-if="view.tone === 'done'" viewBox="0 0 16 16"><path d="M4.2 8.4l2.4 2.3 5.2-5.4" /></svg>
                <svg v-else viewBox="0 0 16 16"><path d="M8 4.6v4M8 11.2v.1" /></svg>
            </span>
            <span class="cp-head">
                <span class="cp-title" role="status" aria-live="polite">{{ view.title }}</span>
                <span v-if="view.detail && view.tone !== 'error'" class="cp-count">{{ view.detail }}</span>
            </span>
        </div>
        <div
            v-if="view.tone === 'live'"
            class="cp-bar"
            role="progressbar"
            :aria-valuenow="view.pct"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-label="view.detail"
        >
            <div class="cp-fill" :style="{ transform: `scaleX(${Math.max(view.pct, 2) / 100})` }" />
        </div>
        <p v-if="view.tone === 'error' && view.detail" class="cp-why">{{ view.detail }}</p>
        <p v-if="view.note" class="cp-note">{{ view.note }}</p>

        <template v-if="reasons.length && view.tone !== 'error'">
            <button type="button" class="cp-more" :aria-expanded="open" data-testid="progress-reasons" @click="open = !open">
                <svg viewBox="0 0 16 16" :class="{ 'is-open': open }" aria-hidden="true"><path d="M6 4l4 4-4 4" /></svg>
                {{ open ? 'Hide why' : 'See why' }}
            </button>
            <div class="agent-disclose" :class="{ 'is-open': open }">
                <div>
                    <ul class="cp-reasons">
                        <li v-for="r in reasons" :key="r.reason">
                            <span class="cp-reason">{{ r.reason }}</span>
                            <span class="cp-reason-n">{{ r.count.toLocaleString('en-US') }}</span>
                            <span v-if="r.samples.length" class="cp-samples">{{ r.samples.join(' · ') }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </template>
    </div>
</template>

<style scoped>
.cp {
    padding: 12px 14px;
    border-radius: var(--agent-radius-md);
    background: var(--agent-card);
    border: 1px solid var(--agent-card-border);
    box-shadow: var(--agent-card-shadow);
}
.cp.is-done { box-shadow: none; background: transparent; padding: 4px 4px 2px; border-color: transparent; }
.cp.is-warn { border-color: rgb(217 119 6 / 0.28); }
.cp.is-error { border-color: rgb(220 38 38 / 0.28); }
.cp-top { display: flex; align-items: center; gap: 8px; min-width: 0; }
.cp-mark {
    display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
    width: 18px; height: 18px; border-radius: 999px;
}
.cp-mark svg { width: 14px; height: 14px; fill: none; stroke: #fff; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.cp-pulse {
    width: 8px; height: 8px; border-radius: 999px; background: var(--agent-accent);
    box-shadow: 0 0 0 0 rgb(251 76 31 / 0.45);
    animation: cp-pulse 1.6s ease-out infinite;
}
@keyframes cp-pulse { to { box-shadow: 0 0 0 7px rgb(251 76 31 / 0); } }
.is-done .cp-mark { background: #10b981; }
.is-warn .cp-mark { background: #d97706; }
.is-error .cp-mark { background: #dc2626; }
.cp-head { display: flex; flex: 1; flex-wrap: wrap; align-items: baseline; column-gap: 10px; row-gap: 2px; min-width: 0; }
.cp-title { font-size: 13.5px; font-weight: 600; color: var(--agent-text-1); }
.is-done .cp-title { font-weight: 500; color: var(--agent-text-2); }
.cp-count { margin-left: auto; font-size: 12.5px; color: var(--agent-text-3); font-variant-numeric: tabular-nums; }
.cp-bar { height: 6px; margin-top: 10px; overflow: hidden; border-radius: 999px; background: var(--agent-hover); }
.cp-fill {
    height: 100%;
    border-radius: inherit;
    transform-origin: left;
    background-image: linear-gradient(90deg, var(--agent-accent-2), var(--agent-accent)),
        linear-gradient(100deg, transparent 30%, rgb(255 255 255 / 0.5) 50%, transparent 70%);
    background-size: 100% 100%, 200% 100%;
    background-blend-mode: overlay;
    animation: cp-sheen 1.8s linear infinite;
    transition: transform 600ms var(--agent-ease, ease);
}
@keyframes cp-sheen { from { background-position: 0 0, 150% 0; } to { background-position: 0 0, -50% 0; } }
.cp-why { margin-top: 6px; font-size: 13px; line-height: 1.45; color: var(--agent-text-2); }
.cp-note { margin-top: 8px; font-size: 12.5px; line-height: 1.45; color: var(--agent-text-3); }
.is-done .cp-note { margin-top: 2px; padding-left: 26px; }
.cp-more {
    display: inline-flex; align-items: center; gap: 5px;
    min-height: 28px; margin: 6px 0 0 -6px; padding: 0 8px; border-radius: 10px;
    font-size: 12.5px; color: var(--agent-text-3);
}
@media (hover: hover) and (pointer: fine) {
    .cp-more:hover { background: var(--agent-hover); color: var(--agent-text-1); }
}
.cp-more svg { width: 12px; height: 12px; fill: none; stroke: currentColor; stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; transition: transform 300ms var(--agent-spring, ease); }
.cp-more svg.is-open { transform: rotate(90deg); }
.cp-reasons {
    display: flex; flex-direction: column; gap: 8px;
    margin: 6px 0 2px; padding: 10px 12px; list-style: none;
    border-radius: 12px; background: var(--agent-hover);
    font-size: 12.5px; line-height: 18px; color: var(--agent-text-2);
}
.cp-reasons li { display: grid; grid-template-columns: 1fr auto; column-gap: 10px; }
.cp-reason { overflow-wrap: anywhere; }
.cp-reason-n { color: var(--agent-text-3); font-variant-numeric: tabular-nums; }
.cp-samples { grid-column: 1 / -1; color: var(--agent-text-4); overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }

/* Phones: lines up with the agent's bubbles. */
.cp.is-bubble { padding: 14px; border-radius: 22px; border: 0; box-shadow: 0 0 0 1px var(--agent-card-border), 0 8px 22px -12px rgb(15 15 15 / 0.22); }
.cp.is-bubble.is-done { box-shadow: none; padding: 2px 4px; }
.cp.is-bubble.is-warn { box-shadow: 0 0 0 1px rgb(217 119 6 / 0.3), 0 8px 22px -12px rgb(15 15 15 / 0.22); }
.cp.is-bubble.is-error { box-shadow: 0 0 0 1px rgb(220 38 38 / 0.3), 0 8px 22px -12px rgb(15 15 15 / 0.22); }
.cp.is-bubble .cp-title { font-size: 15px; }
.cp.is-bubble .cp-count, .cp.is-bubble .cp-note, .cp.is-bubble .cp-more { font-size: 13px; }
.cp.is-bubble .cp-bar { height: 8px; }
.cp.is-bubble .cp-more { min-height: 44px; }

@media (prefers-reduced-motion: reduce) {
    .cp-fill, .cp-pulse { animation: none; }
}
</style>
